import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import axios from 'axios';

import { usePreferences } from '../contexts/PreferencesContext';
import { getCadenceEntry } from '../checkin/queueCadence';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import { getApiErrorMessage } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** "3:00 AM" for a local hour 0-23. */
const formatHour = (hour: number): string => {
  const d = new Date();
  d.setHours(hour, 0, 0, 0);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

/** Mirrors schemas.MIN/MAX_CHECK_IN_BATCH_SIZE. Below three a batch stops
 * being a sitting; above twenty it stops being a subset of the day. */
const MIN_BATCH_SIZE = 3;
const MAX_BATCH_SIZE = 20;
const DEFAULT_BATCH_SIZE = 8;

const BATCH_SIZE_OPTIONS = Array.from(
  { length: MAX_BATCH_SIZE - MIN_BATCH_SIZE + 1 },
  (_, i) => MIN_BATCH_SIZE + i,
);

/** Defensive read of the check_in namespace: an absent row, an empty
 * namespace or an out-of-range value all read as the ratified default. */
const readBatchSize = (namespace: Record<string, unknown> | undefined): number => {
  const value = namespace?.batch_size;
  if (typeof value !== 'number' || !Number.isInteger(value)) return DEFAULT_BATCH_SIZE;
  if (value < MIN_BATCH_SIZE || value > MAX_BATCH_SIZE) return DEFAULT_BATCH_SIZE;
  return value;
};

/**
 * Profile → Check-In Queue: batch size, the daily cutoff hour (account-scoped,
 * when the frozen queue rolls to a new day) and a manual "Recompute now"
 * behind a confirmation — it reshuffles batches the owner may be mid-way
 * through.
 *
 * All explanatory copy is printed verbatim from checkin/queueCadence.json, the
 * single source shared with the Check-In page popover and the user guide.
 */
const CheckInSettingsSection: React.FC = () => {
  const preferences = usePreferences();
  const { cardContentPaddingSx } = useLayoutTokens();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const batchCopy = getCadenceEntry('batch') ?? { key: '', heading: '', body: '' };
  const batchSize = readBatchSize(
    preferences?.settings.check_in as Record<string, unknown> | undefined,
  );

  // Batch size shapes the snapshot at BUILD time, so today's frozen batches are
  // untouched. Rather than leave that as a silent surprise, offer the existing
  // confirmation-gated recompute as an immediate opt-in.
  const changeBatchSize = (size: number) => {
    if (preferences) {
      // Spread the live namespace value: a bare literal would silently drop
      // any sibling key check_in gains later.
      const current = preferences.settings.check_in as Record<string, unknown>;
      preferences.setNamespace('check_in', { ...current, batch_size: size });
    }
    setError(null);
    setFeedback(null);
    setConfirmOpen(true);
  };

  // Real timezone only — mirrors the queue itself (never an as_of_date).
  const tzOffset = useMemo(() => new Date().getTimezoneOffset(), []);

  const recompute = async () => {
    setConfirmOpen(false);
    setRecomputing(true);
    try {
      await axios.post(`${API_BASE_URL}/api/check-ins/queue/recompute`, null, {
        params: { tz_offset: tzOffset },
      });
      setError(null);
      setFeedback("Today's queue was recomputed from your current habits.");
    } catch (err: any) {
      setFeedback(null);
      setError(getApiErrorMessage(err, 'Failed to recompute the check-in queue'));
    } finally {
      setRecomputing(false);
    }
  };

  return (
    <Card>
      <CardContent sx={cardContentPaddingSx}>
        <Typography variant="h6" gutterBottom>
          Check-In Queue
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {batchCopy.body}
        </Typography>

        {preferences && (
          <Box sx={{ mb: 2, maxWidth: 360 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="check-in-batch-size-label">Batch size</InputLabel>
              <Select
                labelId="check-in-batch-size-label"
                label="Batch size"
                value={batchSize}
                onChange={(event) => changeBatchSize(Number(event.target.value))}
              >
                {BATCH_SIZE_OPTIONS.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size} per batch
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {preferences && (
          <Box sx={{ mb: 2, maxWidth: 360 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="check-in-cutoff-hour-label">Daily cutoff hour</InputLabel>
              <Select
                labelId="check-in-cutoff-hour-label"
                label="Daily cutoff hour"
                value={preferences.settings.check_in_cutoff_hour}
                onChange={(event) =>
                  preferences.setTyped('check_in_cutoff_hour', Number(event.target.value))
                }
              >
                {Array.from({ length: 24 }, (_, hour) => (
                  <MenuItem key={hour} value={hour}>
                    {formatHour(hour)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {feedback && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
            {feedback}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Button
          variant="outlined"
          onClick={() => setConfirmOpen(true)}
          disabled={recomputing}
        >
          Recompute now
        </Button>

        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Recompute today&apos;s queue?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This rebuilds today&apos;s queue from your current habits and reshuffles the
              batches — including any you are mid-way through. Habits created today will
              join; anything already answered keeps its credit.
            </DialogContentText>
            <DialogContentText sx={{ mt: 2 }}>{batchCopy.body}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Not now</Button>
            <Button color="warning" variant="contained" onClick={recompute}>
              Recompute
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default CheckInSettingsSection;
